
import { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Tag {
  id: string;
  name: string;
}

interface TagsInputProps {
  floorPlanId: string;
  initialTags?: Tag[];
  onTagsChange?: (tags: Tag[]) => void;
}

export function TagsInput({ floorPlanId, initialTags = [], onTagsChange }: TagsInputProps) {
  const [tags, setTags] = useState<Tag[]>(initialTags);
  const [inputValue, setInputValue] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchTags();
    }
  }, [floorPlanId, user]);

  const fetchTags = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("floor_plan_tags")
        .select(`
          tag_id,
          tags!fk_tag (
            id,
            name
          )
        `)
        .eq("floor_plan_id", floorPlanId);

      if (error) throw error;

      const formattedTags = data
        .map((item: any) => item.tags)
        .filter((tag): tag is Tag => tag !== null);
      
      setTags(formattedTags);
      onTagsChange?.(formattedTags);
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast({
        title: "Fout bij ophalen",
        description: "Er is een fout opgetreden bij het ophalen van de tags.",
        variant: "destructive",
      });
    }
  };

  const handleAddTag = async () => {
    if (!inputValue.trim() || !user) return;

    try {
      // Eerst tag aanmaken of ophalen
      const { data: tagData, error: tagError } = await supabase
        .from("tags")
        .upsert({ 
          name: inputValue.trim(),
          user_id: user.id
        })
        .select()
        .single();

      if (tagError) throw tagError;

      // Dan koppeling maken met plattegrond
      const { error: linkError } = await supabase
        .from("floor_plan_tags")
        .upsert({
          floor_plan_id: floorPlanId,
          tag_id: tagData.id,
        });

      if (linkError) throw linkError;

      setTags([...tags, tagData]);
      setInputValue("");
      onTagsChange?.([...tags, tagData]);

      toast({
        title: "Tag toegevoegd",
        description: "De tag is succesvol toegevoegd.",
      });
    } catch (error) {
      console.error("Error adding tag:", error);
      toast({
        title: "Fout bij toevoegen",
        description: "Er is een fout opgetreden bij het toevoegen van de tag.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      const { error } = await supabase
        .from("floor_plan_tags")
        .delete()
        .eq("floor_plan_id", floorPlanId)
        .eq("tag_id", tagId);

      if (error) throw error;

      const newTags = tags.filter(tag => tag.id !== tagId);
      setTags(newTags);
      onTagsChange?.(newTags);

      toast({
        title: "Tag verwijderd",
        description: "De tag is succesvol verwijderd.",
      });
    } catch (error) {
      console.error("Error removing tag:", error);
      toast({
        title: "Fout bij verwijderen",
        description: "Er is een fout opgetreden bij het verwijderen van de tag.",
        variant: "destructive",
      });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <Badge key={tag.id} variant="secondary" className="gap-1">
            {tag.name}
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0 hover:bg-transparent"
              onClick={() => handleRemoveTag(tag.id)}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Voeg een tag toe..."
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddTag();
            }
          }}
        />
        <Button onClick={handleAddTag} variant="outline">
          Toevoegen
        </Button>
      </div>
    </div>
  );
}
